import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { DocumentEntity, FilterEntity, CurrentCategoryEntity } from './documents.models';
import { Action, createReducer, on } from '@ngrx/store';
import * as DocumentActions from './documents.actions';

export const DOCUMENT_FEATURE_KEY = 'document';

export interface DocumentState extends EntityState<DocumentEntity> {
  current_category: CurrentCategoryEntity | null;
  documents: DocumentEntity[];
  current_document: DocumentEntity | null;
  filters: FilterEntity[];
  loaded: boolean;
  error?: string | null;
  total_items: number;
  current_page: number;
}

export const documentAdapter: EntityAdapter<DocumentEntity> = createEntityAdapter<DocumentEntity>();

export const initialDocumentState: DocumentState = documentAdapter.getInitialState({
  current_category: null,
  current_document: null,
  documents: [],
  filters: [],
  loaded: false,
  total_items: 0,
  current_page: 1,
});

const reducer = createReducer(
  initialDocumentState,
  on(DocumentActions.initDocuments, (state) => ({
    ...state,
    loaded: false,
    error: null,
    current_document: state.current_document ?? null,
  })),
  on(DocumentActions.setCurrentDocument, (state, { document }) => ({
    ...state,
    current_document: document,
    loaded: true,
    error: null,
  })),
  on(DocumentActions.loadDocumentsSuccess, (state, { data }) => ({
    ...state,
    ...data,
    current_document: state.current_document,
    loaded: true,
    error: null,
  })),
  on(DocumentActions.loadDocumentsFailure, (state, { error }) => ({
    ...state,
    loaded: true,
    error,
  })),
  on(DocumentActions.updateDocumentBookmark, (state, { documentId, isBookmarked }) => ({
    ...state,
    // A document that is no longer bookmarked is not part of any collection either
    documents: state.documents.map((doc) =>
      doc.id === documentId
        ? { ...doc, is_bookmarked: isBookmarked, ...(isBookmarked ? {} : { collections: [] }) }
        : doc,
    ),
    current_document:
      state.current_document?.id === documentId
        ? { ...state.current_document, is_bookmarked: isBookmarked }
        : state.current_document,
  })),
  on(DocumentActions.addCollectionToDocuments, (state, { documentIds, collection }) => ({
    ...state,
    documents: state.documents.map((doc) =>
      documentIds.includes(doc.id) && !doc.collections?.some((existing) => existing.id === collection.id)
        ? { ...doc, collections: [...(doc.collections ?? []), collection] }
        : doc,
    ),
  })),
);

export function documentsReducer(state: DocumentState | undefined, action: Action) {
  return reducer(state, action);
}
